import {
    Actions,
    Context
} from "vuex-smart-module";

import {
    AxiosError,
    AxiosResponse
} from "axios";

import {
    Store
} from "vuex";

import {
    store
} from "@/store";

import root
    from "@/store/modules";

import esquireClient from "@/modules/esquire-client";

import WarehouseShopState, {
    WarehouseShopStatus
} from "@/store/modules/warehouse-shop/warehouse-shop-state";

import WarehouseShopGetters from "@/store/modules/warehouse-shop/warehouse-shop-getters";
import WarehouseShopMutations from "@/store/modules/warehouse-shop/warehouse-shop-mutations";

import WarehouseShopData, {
    IWarehouseShopData
} from "@/http/data/warehouse-shop-data";

import WarehouseShopOrderData, {
    IWarehouseShopOrderData
} from "@/http/data/warehouse-shop-order-data";

import WarehouseShopRequest from "@/http/requests/warehouse-shop-request";
import SaveWarehouseOrderRequest from "@/http/requests/create-warehouse-order-request";

interface WarehouseShopErrorResponse {
    message?: string;
    errors?: Record<string, string[]>;
}

export interface SaveWarehouseOrderPayload {
    request: SaveWarehouseOrderRequest;
    orderId?: null|number;
    onSaved?: (order: WarehouseShopOrderData) => void;
}

function unwrapObject<T>(payload: unknown, key?: string): T|null {
    if (!payload || typeof payload !== "object") return null;

    const object = payload as Record<string, unknown>;

    if (key && object[key] && typeof object[key] === "object") {
        return object[key] as T;
    }

    if (object.data && typeof object.data === "object") {
        const data = object.data as Record<string, unknown>;

        if (key && data[key] && typeof data[key] === "object") {
            return data[key] as T;
        }

        return object.data as T;
    }

    return object as T;
}

function unwrapCollection<T>(payload: unknown, key?: string): T[] {
    if (Array.isArray(payload)) return payload as T[];

    if (!payload || typeof payload !== "object") return [];

    const object = payload as Record<string, unknown>;

    if (key && Array.isArray(object[key])) {
        return object[key] as T[];
    }

    if (Array.isArray(object.data)) {
        return object.data as T[];
    }

    if (object.data && typeof object.data === "object") {
        const data = object.data as Record<string, unknown>;

        if (key && Array.isArray(data[key])) {
            return data[key] as T[];
        }
    }

    return [];
}

function resolveErrorMessage(response: AxiosResponse): string {
    const error = response.data as WarehouseShopErrorResponse;

    if (error?.errors) {
        const validationMessage = Object.values(error.errors)
            .flat()
            .find(message => Boolean(message));

        if (validationMessage) return validationMessage;
    }

    if (error?.message) return error.message;

    return `${response.status} ${response.statusText}`;
}

export default class WarehouseShopActions extends Actions<
    WarehouseShopState,
    WarehouseShopGetters,
    WarehouseShopMutations,
    WarehouseShopActions
> {
    private rootContext!: Context<typeof root>;

    $init(_store: Store<typeof store>): void {
        this.rootContext = root.context(_store);

        super.$init(_store);
    }

    async initialize(): Promise<void> {
        this.mutations.setIsInitialized(true);
    }

    async rehydrateStore(): Promise<void> {
        return;
    }

    async initializeForBuilding(building: string): Promise<void> {
        this.mutations.setSelectedBuilding(building);

        await this.actions.loadShop(building);
    }

    async loadShop(building: string): Promise<void> {
        this.mutations.setShopStatus(WarehouseShopStatus.Loading);

        const request = new WarehouseShopRequest(building);

        const response = await esquireClient
            .get("warehouse-shop", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setShop(null);
            this.mutations.setShopStatus(WarehouseShopStatus.Failed);
            await this.actions.notifyMessage("The warehouse shop could not be loaded.");

            return;
        }

        if (response.status === 200) {
            const payload = unwrapObject<IWarehouseShopData>(
                response.data,
                "shop"
            );

            if (!payload) {
                this.mutations.setShop(null);
                this.mutations.setShopStatus(WarehouseShopStatus.Empty);

                return;
            }

            const shop = new WarehouseShopData(payload);

            this.mutations.setShop(shop);
            this.mutations.setShopStatus(
                shop.products.length > 0
                    ? WarehouseShopStatus.Ready
                    : WarehouseShopStatus.Empty
            );

            return;
        }

        this.mutations.setShop(null);

        if (response.status === 401) {
            this.mutations.setShopStatus(WarehouseShopStatus.Unauthenticated);

            return;
        }

        if (response.status === 403) {
            this.mutations.setShopStatus(WarehouseShopStatus.Forbidden);

            return;
        }

        this.mutations.setShopStatus(WarehouseShopStatus.Failed);

        await this.actions.notifyUnhandledError(response);
    }

    async loadOpenOrders(building: string): Promise<void> {
        this.mutations.setOrdersStatus(WarehouseShopStatus.Loading);

        const request = new WarehouseShopRequest(building);

        const response = await esquireClient
            .get("warehouse-shop/orders", {
                params: request.toJson()
            })
            .catch((error: AxiosError) => error.response);

        if (!response) {
            this.mutations.setOpenOrders([]);
            this.mutations.setOrdersStatus(WarehouseShopStatus.Failed);

            return;
        }

        if (response.status === 200) {
            const payload = unwrapCollection<IWarehouseShopOrderData>(
                response.data,
                "orders"
            );

            const orders = payload.map(
                data => new WarehouseShopOrderData(data)
            );

            this.mutations.setOpenOrders(orders);

            this.mutations.setOrdersStatus(
                orders.length > 0
                    ? WarehouseShopStatus.Ready
                    : WarehouseShopStatus.Empty
            );

            return;
        }

        this.mutations.setOpenOrders([]);

        if (response.status === 401) {
            this.mutations.setOrdersStatus(WarehouseShopStatus.Unauthenticated);

            return;
        }

        if (response.status === 403) {
            this.mutations.setOrdersStatus(WarehouseShopStatus.Forbidden);

            return;
        }

        this.mutations.setOrdersStatus(WarehouseShopStatus.Failed);

        await this.actions.notifyUnhandledError(response);
    }

    async saveOrder({
                        request,
                        orderId = null,
                        onSaved
                    }: SaveWarehouseOrderPayload): Promise<void> {
        this.mutations.setIsSaving(true);

        try {
            const response = orderId === null
                ? await esquireClient
                    .post("warehouse-shop/orders", request.toJson())
                    .catch((error: AxiosError) => error.response)
                : await esquireClient
                    .put(`warehouse-shop/orders/${orderId}`, request.toJson())
                    .catch((error: AxiosError) => error.response);

            if (!response) {
                throw new Error("The warehouse order could not be saved.");
            }

            if (response.status !== 200 && response.status !== 201) {
                throw new Error(resolveErrorMessage(response));
            }

            const payload = unwrapObject<IWarehouseShopOrderData>(
                response.data,
                "order"
            );

            if (!payload) {
                throw new Error("The warehouse order response was empty.");
            }

            const order = new WarehouseShopOrderData(payload);

            this.mutations.setCurrentOrder(order);

            if (onSaved) onSaved(order);
        } finally {
            this.mutations.setIsSaving(false);
        }
    }

    clear(): void {
        this.mutations.clear();
    }

    private async notifyUnhandledError(response: AxiosResponse): Promise<void> {
        await this.actions.notifyMessage(resolveErrorMessage(response));
    }

    async notifyMessage(message: string): Promise<void> {
        await this.rootContext.actions.message({
            message,
            type: "error",
            duration: 0,
            showClose: true
        });
    }
}